import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { usePeakLabelLayout, PeakLabelNode } from './hooks/usePeakLabelLayout';
import { PeakLabels, LabelPositionOverride } from './components/PeakLabels';
import { PeakTooltip } from './components/PeakTooltip';
import { YAxis } from './components/YAxis';
import { ChromosomeLabels } from './components/ChromosomeLabels';
import { LocusGeneContextMenu } from './components/LocusGeneContextMenu';
import { ChromosomeSelector } from '../Shared/ChromosomeSelector';
import { getChromosomeLayout } from './layout';
import { exportManhattanPlot } from './utils/exportPlot';
import { analysisIdAtom, regionIdAtom } from '../sharedState';
import { useLocalStorage } from '../hooks/useLocalStorage';
import type { UnifiedLocus, Peak, BurdenResult } from './types';
import './OverviewManhattan.css';

const Y_AXIS_WIDTH = 50;

export interface OverviewManhattanProps {
  /** URL to the genome Manhattan plot image */
  genomeImageUrl: string;
  /** URL to the exome Manhattan plot image */
  exomeImageUrl: string;
  /** Unified loci from the overview API */
  unifiedLoci: UnifiedLocus[];
  /** IDs of peaks to label (when in custom selection mode) */
  selectedPeakIds?: Set<string>;
  /** Whether in custom label mode (true) or default top-N mode (false) */
  customLabelMode?: boolean;
  /** Number of top peaks to label in default mode */
  topN?: number;
  /** Callback when a peak label is clicked */
  onPeakClick?: (node: PeakLabelNode) => void;
  /** Scientifically explicit tooltip for the destination of a peak label click. */
  getPeakDestinationTitle?: (node: PeakLabelNode) => string | undefined;
  /** Callback when a ghost peak dot is clicked to toggle its label */
  onPeakToggle?: (peakId: string, currentLabeledIds: Set<string>) => void;
  /** Show Y-axis with -log10(p) labels */
  showYAxis?: boolean;
  /** Currently selected chromosome ('all' for genome-wide view) */
  contig?: string;
  /** Callback to reset to genome-wide view */
  onResetContig?: () => void;
  /** Callback when a chromosome label is clicked */
  onContigClick?: (contig: string) => void;
  /** Optional key for persisting user label curations to localStorage */
  storageKey?: string;
}

// Significance threshold for GWAS
const GWAS_SIG_THRESHOLD = 5e-8;

/**
 * Convert unified loci to Peak format for usePeakLabelLayout hook.
 * Uses the best p-value (min of genome, exome, and burden tests) for each locus.
 */
function unifiedLociToPeaks(loci: UnifiedLocus[]): Peak[] {
  return loci.map((locus) => {
    // Check if there's a significant GWAS single-variant signal
    const hasGwasSig = (locus.pvalue_genome !== undefined && locus.pvalue_genome < GWAS_SIG_THRESHOLD) ||
                       (locus.pvalue_exome !== undefined && locus.pvalue_exome < GWAS_SIG_THRESHOLD);

    // Start with genome/exome p-values
    let bestPvalue = Math.min(
      locus.pvalue_genome ?? Infinity,
      locus.pvalue_exome ?? Infinity
    );

    // Also check burden test p-values from all genes
    for (const g of locus.genes) {
      if (g.burden_results) {
        for (const b of g.burden_results) {
          if (b.pvalue !== undefined && b.pvalue < bestPvalue) {
            bestPvalue = b.pvalue;
          }
          if (b.pvalue_burden !== undefined && b.pvalue_burden < bestPvalue) {
            bestPvalue = b.pvalue_burden;
          }
          if (b.pvalue_skat !== undefined && b.pvalue_skat < bestPvalue) {
            bestPvalue = b.pvalue_skat;
          }
        }
      }
    }

    // Convert unified genes to GeneInLocus format
    const genes = locus.genes.map((g) => ({
      gene_symbol: g.gene_symbol,
      gene_id: g.gene_id,
      distance_kb: g.distance_kb,
      coding_variant_count:
        (g.genome_coding_hits?.lof ?? 0) +
        (g.genome_coding_hits?.missense ?? 0) +
        (g.exome_coding_hits?.lof ?? 0) +
        (g.exome_coding_hits?.missense ?? 0),
      lof_count:
        (g.genome_coding_hits?.lof ?? 0) + (g.exome_coding_hits?.lof ?? 0) || undefined,
      missense_count:
        (g.genome_coding_hits?.missense ?? 0) + (g.exome_coding_hits?.missense ?? 0) || undefined,
      best_coding_csq: g.best_coding_csq,
      best_coding_hgvsp: g.best_coding_hgvsp,
      best_coding_hgvsc: g.best_coding_hgvsc,
      best_coding_ac: g.best_coding_ac,
      best_coding_variant_id: g.best_coding_variant_id,
      best_coding_pvalue: g.best_coding_pvalue,
      best_coding_beta: g.best_coding_beta,
      burden_results: g.burden_results,
    }));

    return {
      locus_id: locus.locus_id,
      variant_count: locus.variant_count,
      start: locus.start,
      stop: locus.stop,
      contig: locus.contig,
      position: locus.position,
      pvalue: bestPvalue === Infinity ? 1 : bestPvalue,
      genes,
      isBurdenOnly: !hasGwasSig,
    };
  });
}

/**
 * Overview Manhattan Plot Component.
 *
 * Composites genome and exome Manhattan plots using CSS overlay.
 * Uses CSS filter to color-shift the exome plot for visual distinction.
 */
export const OverviewManhattan: React.FC<OverviewManhattanProps> = ({
  genomeImageUrl,
  exomeImageUrl,
  unifiedLoci,
  selectedPeakIds,
  customLabelMode = false,
  topN = 10,
  onPeakClick,
  getPeakDestinationTitle,
  onPeakToggle,
  showYAxis = true,
  contig = 'all',
  onResetContig,
  onContigClick,
  storageKey,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const exomeImageRef = useRef<HTMLImageElement>(null);
  const exportContainerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredPeakNode, setHoveredPeakNode] = useState<PeakLabelNode | null>(null);
  const [peakCursor, setPeakCursor] = useState({ x: 0, y: 0 });
  const [genomeLoaded, setGenomeLoaded] = useState(false);
  const [exomeLoaded, setExomeLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [prevUrls, setPrevUrls] = useState({ genome: genomeImageUrl, exome: exomeImageUrl });
  const [isExporting, setIsExporting] = useState(false);

  if (genomeImageUrl !== prevUrls.genome || exomeImageUrl !== prevUrls.exome) {
    setPrevUrls({ genome: genomeImageUrl, exome: exomeImageUrl });
    setGenomeLoaded(false);
    setExomeLoaded(false);
    setImageError(false);
  }
  // Label position overrides from user dragging
  const [labelOverrides, setLabelOverrides] = useLocalStorage<Record<string, LabelPositionOverride>>(storageKey ? `${storageKey}_overrides` : undefined, {});
  // Settings menu state
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [useDogLegStems, setUseDogLegStems] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const currentAnalysisId = useRecoilValue(analysisIdAtom);
  const regionId = useRecoilValue(regionIdAtom);

  // Compute normalized x center of the active region for the arrow marker
  const activeRegionX = useMemo(() => {
    if (!regionId) return undefined;
    const parts = regionId.split('-');
    if (parts.length < 3) return undefined;
    const rContig = parts[0];
    const rStart = parseInt(parts[1], 10);
    const rEnd = parseInt(parts.slice(2).join('-'), 10);
    if (isNaN(rStart) || isNaN(rEnd)) return undefined;
    const layout = getChromosomeLayout(contig);
    const x1 = layout.getX(rContig, rStart);
    const x2 = layout.getX(rContig, rEnd);
    if (x1 === null || x2 === null) return undefined;
    return (x1 + x2) / 2;
  }, [regionId, contig]);

  // Unified context menu state - can include locus, gene, or multiple genes
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    locus?: { contig: string; position: number; start?: number; stop?: number };
    gene?: { geneId: string; geneSymbol: string };
    genes?: Array<{ geneId: string; geneSymbol: string; burdenTypes?: string[]; hasCoding?: boolean; relationship: 'implicated' }>;
  } | null>(null);

  // Convert unified loci to peaks format for label layout
  const peaks = useMemo(() => unifiedLociToPeaks(unifiedLoci), [unifiedLoci]);

  // Compute peak label positions and required label area height
  // All peaks are passed; the hook determines which are labeled vs ghost
  const { nodes: peakLabelNodes, labelAreaHeight } = usePeakLabelLayout(
    peaks,
    dimensions.width,
    dimensions.height,
    contig,
    customLabelMode ? 500 : topN,
    customLabelMode,
    selectedPeakIds ?? new Set(),
  );

  // Observe image size changes for responsive scaling
  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    observer.observe(image);
    return () => observer.disconnect();
  }, [genomeLoaded]);

  const handlePeakHover = useCallback((node: PeakLabelNode | null, x?: number, y?: number) => {
    setHoveredPeakNode(node);
    if (x !== undefined && y !== undefined) {
      setPeakCursor({ x, y });
    }
  }, []);

  // Handle label drag end - store the new position
  const handleLabelDragEnd = useCallback((id: string, x: number, y: number) => {
    setLabelOverrides((prev) => ({
      ...prev,
      [id]: { x, y },
    }));
  }, []);

  // Reset all label positions to auto-layout
  const handleResetLayout = useCallback(() => {
    setLabelOverrides({});
  }, []);

  // Export the plot to PNG
  const handleExportPlot = useCallback(async () => {
    if (!exportContainerRef.current) return;
    setIsExporting(true);
    try {
      await exportManhattanPlot(exportContainerRef.current, 'manhattan-plot.png');
    } catch (err) {
      console.error('Failed to export plot:', err);
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleGenomeLoad = useCallback(() => {
    setGenomeLoaded(true);
    setImageError(false);
  }, []);

  const handleExomeLoad = useCallback(() => {
    setExomeLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // If the images are already cached, onLoad might fire before React mounts the component.
  // Manually check if they are complete to ensure loading state updates.
  useEffect(() => {
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
      handleGenomeLoad();
    }
    if (exomeImageRef.current?.complete && exomeImageRef.current.naturalWidth > 0) {
      handleExomeLoad();
    }
  }, [genomeImageUrl, exomeImageUrl, handleGenomeLoad, handleExomeLoad]);

  if (imageError) {
    return (
      <div className="manhattan-container">
        <div className="manhattan-error">Failed to load Manhattan plot images</div>
      </div>
    );
  }

  const hasPeaks = peakLabelNodes.length > 0;
  const imagesLoaded = genomeLoaded && exomeLoaded;

  return (
    <div ref={containerRef} className="manhattan-container">
      {/* Plot header with legend, stats, and settings */}
      {imagesLoaded && (
        <div
          className="manhattan-plot-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
            marginLeft: showYAxis ? Y_AXIS_WIDTH : 0,
            padding: '6px 0',
            fontSize: 11,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {/* Left side: Stats */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ color: 'var(--theme-text-muted)' }}>
              <strong style={{ color: 'var(--theme-text)' }}>{unifiedLoci.length.toLocaleString()}</strong> loci
            </span>
            <span style={{ color: 'var(--theme-text-muted)' }}>
              P &lt; 5e-8
            </span>
            {/* Legend */}
            <span style={{ color: 'var(--theme-text-muted)' }}>
              <span style={{ color: '#d32f2f' }}>●</span> pLoF
            </span>
            <span style={{ color: 'var(--theme-text-muted)' }}>
              <span style={{ color: '#e68a00' }}>●</span> Missense
            </span>
            <span style={{ color: 'var(--theme-text-muted)' }}>
              <span style={{ color: '#7b1fa2' }}>◆</span> Burden-only
            </span>
          </div>
          {/* Right side: Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Reset Layout button - only show if there are overrides */}
            {Object.keys(labelOverrides).length > 0 && (
              <button
                onClick={handleResetLayout}
                style={{
                  fontSize: 11,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  background: 'var(--theme-surface, #fff)',
                  color: 'var(--theme-text, #333)',
                  border: '1px solid var(--theme-border, #ccc)',
                  borderRadius: 3,
                }}
                title="Reset all labels to auto-positioned layout"
              >
                Reset layout
              </button>
            )}
            {/* Settings menu button */}
            <div style={{ position: 'relative' }}>
              <button
                ref={settingsButtonRef}
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                style={{
                  fontSize: 11,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  background: showSettingsMenu ? 'var(--theme-surface-alt, #f5f5f5)' : 'var(--theme-surface, #fff)',
                  color: 'var(--theme-text, #333)',
                  border: '1px solid var(--theme-border, #ccc)',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                title="Plot settings"
              >
                <span style={{ fontSize: 13 }}>⚙</span> Settings
              </button>
              {/* Settings dropdown */}
              {showSettingsMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    background: 'var(--theme-surface, #fff)',
                    border: '1px solid var(--theme-border, #ccc)',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: 200,
                    padding: '8px 0',
                  }}
                  onMouseLeave={() => setShowSettingsMenu(false)}
                >
                  {/* Line style toggle */}
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--theme-border, #eee)' }}>
                    <div style={{ fontSize: 10, color: 'var(--theme-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Leader Line Style
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={useDogLegStems}
                        onChange={(e) => setUseDogLegStems(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Dog-leg / Crankshaft</span>
                    </label>
                    <div style={{ fontSize: 10, color: 'var(--theme-text-muted)', marginTop: 4 }}>
                      {useDogLegStems ? 'Lines bend at right angles' : 'Straight lines (default)'}
                    </div>
                  </div>
                  {/* Export button */}
                  <div style={{ padding: '8px 12px' }}>
                    <button
                      onClick={() => {
                        setShowSettingsMenu(false);
                        handleExportPlot();
                      }}
                      disabled={isExporting}
                      style={{
                        width: '100%',
                        fontSize: 12,
                        padding: '8px 12px',
                        cursor: isExporting ? 'wait' : 'pointer',
                        background: 'var(--theme-primary, #262262)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        opacity: isExporting ? 0.7 : 1,
                      }}
                      title="Export plot as PNG image"
                    >
                      {isExporting ? 'Exporting...' : '📷 Export as PNG'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div ref={exportContainerRef} className="manhattan-plot-row" style={{ display: 'flex' }}>
        {/* Y-Axis */}
        {showYAxis && imagesLoaded && (
          <div
            style={{
              width: Y_AXIS_WIDTH,
              flexShrink: 0,
              position: 'relative',
              marginTop: labelAreaHeight,
            }}
          >
            <YAxis height={dimensions.height} width={Y_AXIS_WIDTH} />
          </div>
        )}

        <div
          className="manhattan-image-wrapper"
          style={{ flex: 1, position: 'relative' }}
        >
          {/* Spacer for label area */}
          {hasPeaks && <div style={{ height: labelAreaHeight }} />}

          {/* Composite image wrapper */}
          <div className="overview-image-wrapper">
            {/* Base image: Genome Manhattan */}
            <img
              ref={imageRef}
              src={genomeImageUrl}
              alt="Genome Manhattan Plot"
              className="overview-image-base manhattan-image"
              onLoad={handleGenomeLoad}
              onError={handleImageError}
              draggable={false}
              style={{ opacity: genomeLoaded ? 1 : 0.3, transition: 'opacity 0.2s' }}
            />

            {/* Overlay image: Exome Manhattan with CSS filter */}
            <img
              ref={exomeImageRef}
              src={exomeImageUrl}
              alt="Exome Manhattan Plot"
              className="overview-image-overlay"
              onLoad={handleExomeLoad}
              onError={handleImageError}
              draggable={false}
              style={{ opacity: exomeLoaded ? 0.85 : 0, transition: 'opacity 0.2s' }}
            />
          </div>

          {/* Peak labels SVG - spans label area and plot */}
          {imagesLoaded && dimensions.width > 0 && hasPeaks && (
            <svg
              className="manhattan-peak-overlay"
              width={dimensions.width}
              height={dimensions.height + labelAreaHeight}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
              }}
            >
              <PeakLabels
                nodes={peakLabelNodes}
                plotHeight={dimensions.height}
                labelAreaHeight={labelAreaHeight}
                hoveredHitPosition={null}
                onPeakHover={handlePeakHover}
                onPeakClick={onPeakClick}
                getPeakDestinationTitle={getPeakDestinationTitle}
                onPeakToggle={onPeakToggle}
                onPeakContextMenu={(node, clientX, clientY) => {
                  setContextMenu({
                    x: clientX,
                    y: clientY,
                    locus: { contig: node.peak.contig, position: node.peak.position, start: node.peak.start, stop: node.peak.stop },
                    genes: node.implicatedGenes.map((g) => ({
                      geneId: g.geneId,
                      geneSymbol: g.geneSymbol,
                      burdenTypes: g.burdenTypes,
                      hasCoding: g.hasCoding,
                      relationship: 'implicated' as const,
                    })),
                  });
                }}
                labelOverrides={labelOverrides}
                onLabelDragEnd={handleLabelDragEnd}
                useDogLegStems={useDogLegStems}
              />
            </svg>
          )}

          {/* Peak Tooltip */}
          {hoveredPeakNode && imagesLoaded && (
            <PeakTooltip
              node={hoveredPeakNode}
              x={peakCursor.x}
              y={peakCursor.y}
              containerWidth={dimensions.width}
            />
          )}
        </div>
      </div>

      {/* Chromosome labels */}
      {imagesLoaded && dimensions.width > 0 && (
        <div style={{ marginLeft: showYAxis ? Y_AXIS_WIDTH : 0 }}>
          <ChromosomeLabels width={dimensions.width} contig={contig} onContigClick={onContigClick} activeRegionX={activeRegionX} />
        </div>
      )}

      {/* Chromosome controls bar */}
      {imagesLoaded && (
        <div className="overview-stats" style={{ marginLeft: showYAxis ? Y_AXIS_WIDTH : 0 }}>
          {contig !== 'all' && onResetContig && (
            <div className="overview-stats-item">
              <button
                onClick={onResetContig}
                style={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  border: '1px solid var(--theme-border)',
                  background: 'var(--theme-surface)',
                  fontSize: '12px',
                }}
              >
                &larr; Back to All
              </button>
            </div>
          )}
          <div className="overview-stats-item">
            <span className="overview-stats-label">Chromosome:</span>
            <ChromosomeSelector />
          </div>
        </div>
      )}

      {/* Loading state */}
      {!imagesLoaded && !imageError && (
        <div className="manhattan-loading">Loading Manhattan plots...</div>
      )}

      {/* Unified context menu */}
      {contextMenu && (
        <LocusGeneContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          locus={contextMenu.locus}
          gene={contextMenu.gene}
          genes={contextMenu.genes}
          currentPhenotypeDescription={currentAnalysisId || undefined}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default OverviewManhattan;
